// src/orderApi.js
//
// The things that are done *to* an order after it exists: issuing its invoice,
// sending the confirmation again, and — for admins — the proof of payment
// attached to it.
//
// All of it goes through Cloud Functions rather than Firestore, because all of
// it is an action rather than a read. An invoice number has to be issued
// exactly once, an email must only ever go to the address stored on the order,
// and the storage bucket holding proofs of payment refuses the browser
// outright. The client sends an order id and nothing else that decides
// anything.

import axios from 'axios';
import { auth } from './firebase';

// Cloud Run URLs, not the *.cloudfunctions.net aliases. The alias validates any
// Authorization: Bearer header as a Google IAM token and rejects our Firebase
// ID token with a 401 carrying no CORS headers, which the browser then reports
// as a CORS failure. The run.app URL passes the header through to the function.
const ENDPOINTS = {
  invoice: 'https://orderinvoice-bb4d2hrpta-uc.a.run.app',
  resend: 'https://resendorderemail-bb4d2hrpta-uc.a.run.app',
  proof: 'https://orderproof-bb4d2hrpta-uc.a.run.app',
};

/**
 * What the proof-of-payment endpoint will accept, repeated here so a file that
 * is never going to be stored is refused before it is read and sent. The
 * function checks the same things again — this side is a courtesy, not the
 * guard.
 */
export const PROOF_MAX_BYTES = 6 * 1024 * 1024;

export const PROOF_ACCEPT =
  'application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif';

const PROOF_TYPES = PROOF_ACCEPT.split(',');

/** The longest written proof the endpoint will keep. */
export const PROOF_TEXT_MAX = 1000;

/** The reason this file cannot be attached, or null when it can. */
export function proofProblem(file) {
  if (!file) return 'Choose a file to attach.';
  // Some browsers hand over an empty type for HEIC; the extension is all we
  // have to go on there, and the function re-checks the type regardless.
  const type = String(file.type || '').toLowerCase();
  const looksHeic = /\.(heic|heif)$/i.test(file.name || '');
  if (type !== '' && !PROOF_TYPES.includes(type) && !looksHeic) {
    return 'Attach a PDF or a photograph (JPEG, PNG, WEBP or HEIC).';
  }
  if (type === '' && !looksHeic) {
    return 'That file type cannot be attached. Use a PDF or a photograph.';
  }
  if (file.size > PROOF_MAX_BYTES) {
    return 'That file is larger than 6 MB. Please attach a smaller one.';
  }
  if (file.size === 0) return 'That file is empty.';
  return null;
}

/** The signed-in caller's ID token, or a refusal the caller can show. */
const idToken = async () => {
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error('Please sign in again to continue.');
  return token;
};

/**
 * Unwraps the message a function sends back.
 *
 * The functions answer failures as `{ message }` with a real status code, so
 * that is what a customer should read — falling back to the transport error
 * only when there is no reply to read.
 */
export const messageFrom = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

/** POSTs to a function as the signed-in caller. Also used by photoApi.js. */
export const post = async (url, body) => {
  const token = await idToken();
  const response = await axios.post(url, body, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    timeout: 45000,
  });
  return response.data;
};

/**
 * Issues (or re-serves) the invoice for an order and returns it with the PDF
 * decoded into a Blob ready to save.
 *
 * `taxDetails` is optional and may be partial. Sending it again with different
 * values corrects the invoice; the number and issue date do not change, because
 * an invoice number that moved between two downloads would be a problem for
 * whoever filed the first one.
 */
export async function requestInvoice(orderId, { taxDetails, sendEmail = false } = {}) {
  try {
    const data = await post(ENDPOINTS.invoice, { orderId, taxDetails, sendEmail });
    return { ...data, blob: base64ToBlob(data.pdfBase64, 'application/pdf') };
  } catch (error) {
    throw new Error(messageFrom(error, 'We could not produce your invoice. Please try again.'));
  }
}

/** Sends the order confirmation again, to the address recorded on the order. */
export async function resendConfirmation(orderId) {
  try {
    return await post(ENDPOINTS.resend, { orderId });
  } catch (error) {
    throw new Error(messageFrom(error, 'We could not send that email. Please try again.'));
  }
}

/**
 * Attaches a proof of payment to an order, replacing one already there.
 *
 * The file is sent base64 inside the JSON body rather than uploaded to Cloud
 * Storage from the browser: the bucket refuses the client entirely, because
 * storage rules cannot read the `admins` collection and so cannot tell an admin
 * from anybody else who happens to be signed in.
 */
export async function attachProofOfPayment(orderId, file) {
  const problem = proofProblem(file);
  if (problem) throw new Error(problem);
  try {
    return await post(ENDPOINTS.proof, {
      orderId,
      action: 'upload',
      filename: file.name,
      contentType: contentTypeOf(file),
      dataBase64: await fileToBase64(file),
    });
  } catch (error) {
    throw new Error(messageFrom(error, 'We could not attach that file. Please try again.'));
  }
}

/**
 * Records a proof of payment that is not a file: `note` for a bank reference or
 * a payment notification typed in, `confirmed` for money we saw arrive
 * ourselves — cash, usually — with an optional note on who took it. Replaces
 * whatever proof the order had, a file included.
 */
export async function recordProofOfPayment(orderId, { kind, text = '' }) {
  try {
    return await post(ENDPOINTS.proof, { orderId, action: 'record', kind, text });
  } catch (error) {
    throw new Error(messageFrom(error, 'We could not record that proof. Please try again.'));
  }
}

/** The attached proof, decoded into a Blob ready to open or save. */
export async function fetchProofOfPayment(orderId) {
  try {
    const data = await post(ENDPOINTS.proof, { orderId, action: 'fetch' });
    return { ...data, blob: base64ToBlob(data.fileBase64, data.contentType) };
  } catch (error) {
    throw new Error(messageFrom(error, 'We could not fetch that file. Please try again.'));
  }
}

/** Takes the proof off the order and deletes the stored file. */
export async function removeProofOfPayment(orderId) {
  try {
    return await post(ENDPOINTS.proof, { orderId, action: 'remove' });
  } catch (error) {
    throw new Error(messageFrom(error, 'We could not remove that file. Please try again.'));
  }
}

/** A type the endpoint recognises, inferred when the browser offers none. */
function contentTypeOf(file) {
  const type = String(file.type || '').toLowerCase();
  if (PROOF_TYPES.includes(type)) return type;
  return /\.heif$/i.test(file.name || '') ? 'image/heif' : 'image/heic';
}

/**
 * The file's bytes as base64.
 *
 * FileReader's data URL is read in one go and the prefix trimmed off, rather
 * than walking the bytes by hand — a six megabyte photograph is millions of
 * iterations of a loop the browser already has a native path for.
 */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('That file could not be read.'));
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma === -1 ? '' : result.slice(comma + 1));
    };
    reader.readAsDataURL(file);
  });
}

/** The PDF arrives base64 encoded inside JSON, because the request is authed. */
function base64ToBlob(base64, type) {
  const binary = atob(base64 || '');
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type });
}

/**
 * Saves a Blob to the customer's downloads.
 *
 * The object URL is revoked on the next frame rather than immediately: Safari
 * cancels a download whose URL is released before it has started reading it.
 */
export function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
